/* eslint-disable indent */
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { ColumnFilterElementTemplateOptions } from 'primereact/column';
import { ReactNode } from 'react';

type BaseColumnOptions = {
  field: string;
  labelKey: string;
  minWidth?: string;
  isDate?: boolean;
  dateOnly?: boolean;
  dateFilterTemplate?: (options: ColumnFilterElementTemplateOptions) => ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bodyTemplate?: (rowData: any) => ReactNode;
  sortable?: boolean;
  filter?: boolean;
};

// Date-only template (DD/MM/YYYY)
const makeDateOnlyBodyTemplate = (field: string) => (rowData: Record<string, unknown>) => {
  const dateValue = rowData[field] as Date | string | undefined;
  const date = dateValue ? new Date(dateValue) : null;
  return !date || Number.isNaN(date.getTime()) ? undefined : format(date, 'dd/MM/yyyy');
};

// Date-time template (DD/MM/YYYY HH:mm)
const makeDateTimeBodyTemplate = (field: string) => (rowData: Record<string, unknown>) => {
  const dateValue = rowData[field] as Date | string | undefined;
  const date = dateValue ? new Date(dateValue) : null;
  return !date || Number.isNaN(date.getTime()) ? undefined : format(date, 'dd/MM/yyyy HH:mm');
};

const createBaseColumn = (
  t: TFunction,
  {
    field,
    labelKey,
    minWidth = '10rem',
    isDate = false,
    dateOnly = false,
    dateFilterTemplate,
    bodyTemplate,
    sortable = true,
    filter = true,
  }: BaseColumnOptions,
) => {
  const base: Record<string, unknown> = {
    field,
    header: t(`reports.${labelKey}`),
    filter,
    filterField: field,
    showFilterOperator: false,
    showAddButton: false,
    showFilterMatchModes: false,
    style: { minWidth },
    sortable,
  };

  if (isDate) {
    base.dataType = 'date';
    base.body =
      bodyTemplate ??
      (dateOnly ? makeDateOnlyBodyTemplate(field) : makeDateTimeBodyTemplate(field));
    base.filterElement = dateFilterTemplate;
  }

  if (bodyTemplate && !isDate) {
    base.body = bodyTemplate;
  }

  return base;
};

export const getFlightDateColumnProps = (
  t: TFunction,
  dateFilterTemplate: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'flightDate',
    labelKey: 'flightDate',
    minWidth: '12rem',
    isDate: true,
    dateOnly: true,
    dateFilterTemplate,
  });

export const getCarrierCodeColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'carrierCode', labelKey: 'carrier' });

export const getFlightNumberColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'flightNumber', labelKey: 'flightNumber' });

export const getDepartureAirportColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'departureAirport', labelKey: 'departure' });

export const getArrivalAirportColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'arrivalAirport', labelKey: 'arrival' });

export const getAircraftRegistrationColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'aircraftRegistration', labelKey: 'registration' });

// Commercial-specific columns
export const getSupervisorEmployeeIdColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'supervisorEmployeeId',
    labelKey: 'evaluations.supervisorEmployeeId',
  });

export const getEvaluatedEmployeeIdColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'evaluatedEmployeeId',
    labelKey: 'evaluations.evaluatedEmployeeId',
  });

export const getEvaluatedEmployeeNameColumnProps = (t: TFunction) =>
  createBaseColumn(t, {
    field: 'evaluatedEmployeeName',
    labelKey: 'evaluations.evaluatedEmployeeName',
    minWidth: '14rem',
    bodyTemplate: (rowData: Record<string, unknown>) => {
      const firstName = (rowData.evaluatedEmployeeName as string | undefined)?.trim() ?? '';
      const lastName = (rowData.evaluatedEmployeeLastName as string | undefined)?.trim() ?? '';
      return `${firstName} ${lastName}`.trim();
    },
    sortable: false,
    filter: false,
  });

// Flight-specific columns
export const getPurserEmployeeIdColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'purserEmployeeId', labelKey: 'evaluations.purserEmployeeId' });

export const getReceptionDateColumnProps = (
  t: TFunction,
  dateFilterTemplate: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'receptionDate',
    labelKey: 'evaluations.receptionDate',
    minWidth: '12rem',
    isDate: true,
    dateFilterTemplate,
  });
